import React, {useEffect, useState} from 'react'
import { useLocation } from 'react-router';
import FixedLoader from "../../../components/FixedLoader/FixedLoader";

import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";

import {usePersistedState} from "../../../hooks";
import {HYGIENIST_TYPES, HygienistProps, PositionProps, SkillsProps, UserAccountProps} from "utils/types";
import {entities} from "../../../reduxStore";

import {AboutMe} from "./components/AboutMe";
import {Profile} from "./components/Profile";
import {Qualification} from "./components/Qualification";
import Experience from "./components/Experience";
import Api from "../../../api";
import styles from "../../dentist/PostJobPage/PostJob.module.scss";
import {CrossButtonBlack} from "../../../components/Buttons/CrossButton";
import clsx from "clsx";
import {StyledButton} from "../../../components/Buttons/DefaultButton/DefaultButton.styles";
import {SimpleModal} from "../../../components/Modal";
import {exitReact} from "../../../utils";
import {useHistory} from "react-router";
import * as ls from "local-storage";
import { HygienistSkill } from 'utils/constants';

dayjs.extend(isToday)

// @ts-ignore
const TempStars = window.TempStars as any;


interface LocationState {
    isOpenPositionModal?: boolean;
}

const ProfilePage = () => {
    const history = useHistory();
    const location = useLocation<LocationState>();
    const hygienist = usePersistedState<{
        skills: HygienistSkill[],
    } & HygienistProps>({
        entityName: entities.hygienist
    })[0];

    const setHygienist = usePersistedState({
        entityName: entities.hygienist,
    })[1]

    const [about, setAbout] = useState<string>(hygienist?.about || '');
    const [isAboutChanged, setIsAboutChanged] = useState<boolean>(false);
    const [positionsObj, setPositionsObj] = useState<PositionProps[]>([])
    const [loading, setLoading] = useState(false)
    const [isOpenProfile, setOpenProfile] = useState<boolean>(true);
    const [isOpenAboutModal, setOpenAboutModal] = useState<boolean>(false);
    const [isOpenSuccessModal, setOpenSuccessModal] = useState<boolean>(false);
    const [isOpenQualificationModal, setOpenQualificationModal] = useState<boolean>(false);
    const [isOpenPositionModal, setOpenPositionModal] = useState(
        location.state?.isOpenPositionModal || false
    );
    const isHygienistOrAssistant = hygienist?.type in [HYGIENIST_TYPES.HYGIENIST, HYGIENIST_TYPES.ASSISTANT];

    useEffect(() => {
        if (hygienist) {
            setOpenProfile(!isOpenAboutModal && !isOpenPositionModal && !isOpenQualificationModal);
        }
    }, [hygienist, isOpenAboutModal, isOpenPositionModal, isOpenQualificationModal]);

    useEffect(() => {
        const fetchWorkExperience = async () => {
            try {
                setLoading(true)
                const workExperience = await Api.getWorkExperience()
                if (workExperience.length) {
                    setPositionsObj(workExperience)
                }
                setLoading(false)
            } catch (e) {
                console.log('Error fetching work experience', e)
            }
        }
        fetchWorkExperience()
    }, []);


    const onSubmit = async (e: any) => {
        setLoading(true)
        try {
            await Api.updateHygienistAccount({
                address: e.address,
                city: e.city,
                province: e.province,
                postalCode: e.postalCode,
                phone: e.phone,
                photoUrl: e.photoUrl,
                etransferEmail: e.eTransferEmail,
                about: about,
                WeeklyNewsletter: !!e.weeklyNewsletter ? 1 : 0,
                PriorityNotifications: !!e.priorityNotifications ? 1 : 0,
                receivePostedShifts: !!e.receivePostedShifts ? 1 : 0,
            })
            if (String(hygienist?.phone) !== String(e.phone)) {
                await Api.updateHygienistAccount({
                    verified: 0
                })
            }
            const user = await Api.getUser()
            ls.set<UserAccountProps>('userAccount', user as UserAccountProps)
            setHygienist(user?.hygienist)
            TempStars.App.loadMenu(user)
            setIsAboutChanged(false)
            setOpenSuccessModal(true)
        } catch (e) {
            console.log('Error updating hygienist account', e)
        } finally {
            setLoading(false)
        }
    }

    const onSaveAbout = (about: string) => {
        setIsAboutChanged(true)
        setAbout(about)
    }

    const onExit = () => {
        setOpenSuccessModal(false)
        history.push('loading')
        if(isHygienistOrAssistant) {
            exitReact('home')
        } else {
            history.push('jobPostsList')
        }
    }

    return (<>

        <SimpleModal isOpen={isOpenSuccessModal}>
            <>
                <div className={styles.titleContainer}>
                    <div className={styles.popupTitle}>Profile updated successfully!</div>
                    <CrossButtonBlack className={clsx("no-fastclick", styles.crossButtonDark)}
                                      onClick={() => setOpenSuccessModal(false)}/>
                </div>
                <StyledButton
                    variant="contained"
                    size="large"
                    onClick={onExit}
                >
                    Go Home
                </StyledButton>
                <StyledButton
                    variant="outlined"
                    size="large"
                    onClick={() => setOpenSuccessModal(false)}
                >
                    Close
                </StyledButton>
            </>
        </SimpleModal>


        {isOpenProfile
            ? <Profile
                isAboutChanged={isAboutChanged}
                workExperience={positionsObj}
                hygienist={{...hygienist, about}}
                setOpenSuccessModal={() => setOpenSuccessModal(!isOpenSuccessModal)}
                setOpenAboutModal={() => setOpenAboutModal(!isOpenAboutModal)}
                setOpenPositionModal={() => setOpenPositionModal(!isOpenPositionModal)}
                setOpenQualificationModal={() => setOpenQualificationModal(!isOpenQualificationModal)}
                onSubmit={onSubmit}
            />
            : null}

        <AboutMe
            about={about}
            name={hygienist?.firstName}
            isOpen={isOpenAboutModal}
            onSaveAbout={onSaveAbout}
            onClose={() => setOpenAboutModal(false)}
        />

        <Qualification
            isOpen={isOpenQualificationModal}
            hygienist={hygienist}
            onClose={() => setOpenQualificationModal(false)}
        />

        <Experience
            positionsObj={positionsObj}
            setPositionsObj={setPositionsObj}
            country={hygienist.country || 0}
            isOpen={isOpenPositionModal}
            onClose={() => setOpenPositionModal(false)}
            isHygienistOrAssistant={isHygienistOrAssistant}
        />

        <FixedLoader
            loading={loading}
        />
    </>)
}

export default ProfilePage;
